import type { FilterInput } from "@/services/entity-instance.service";
import {
  deleteEntitySubscription,
  parseEntityEventOperation,
  upsertEntitySubscription,
  type EntityEventKind,
} from "@/services/subscription.service";

/**
 * A single websocket connection shared by every subscription in the tab.
 *
 * The first `subscribe()` mints one `connectionId`, registers the subscription
 * with the backend (which returns the public `webserviceUrl`), then opens one
 * websocket. Every later subscription reuses that same `connectionId`, so they
 * all ride the one socket. Incoming `entity.<kind>.<Entity>` events are routed
 * to the registering subscription by their `subscriptionId`.
 *
 * This is a module-level singleton, mirroring how `providers.tsx` keeps a single
 * browser QueryClient. It is framework-agnostic; `useEntitySubscription` is the
 * React binding on top of it.
 */

export type SubscriptionStatus =
  | "idle"
  | "connecting"
  | "authenticating"
  | "authenticated"
  | "reconnecting"
  | "closed";

/**
 * Subscription-scoped result-set verdict sent by the websocket-service:
 * `inResultSet: false` means the row was updated but no longer appears in this
 * subscription's filtered query (filter mismatch or lost read permission) —
 * the client should drop it from the list. Absent = row is in the result set.
 */
export interface EventMembership {
  inResultSet?: boolean;
}

export interface EntitySubscribeArgs {
  entityName: string;
  /** GraphQL selection body for the per-event `fields { ... }` projection. */
  fieldsSelection: string;
  filter?: FilterInput;
  onCreate?: boolean;
  onUpdate?: boolean;
  onDelete?: boolean;
  onEvent: (
    kind: EntityEventKind,
    data: Record<string, unknown>,
    membership?: EventMembership,
  ) => void;
  /**
   * Invoked after a genuine RE-connect once all subscriptions have been
   * re-registered, so the caller can backfill events that were missed while the
   * socket was down (beyond the backend's queue window). Not called on the first
   * connect.
   */
  onResync?: () => void;
}

export interface EntitySubscriptionHandle {
  /** Update the subscription in place (e.g. new filter) — re-upserts the same id. */
  update: (args: Partial<Pick<EntitySubscribeArgs, "filter" | "fieldsSelection">>) => Promise<void>;
  /** Drop the local handler; the socket stays open for other subscriptions. */
  unsubscribe: () => void;
}

interface RegisteredSubscription extends EntitySubscribeArgs {
  /** Server-assigned subscription id, once the upsert resolves. */
  serverId?: string;
  /** In-flight/last upsert, so update() and reconnect chain rather than race. */
  registration?: Promise<void>;
}

interface WsMessage {
  operation: string;
  subscriptionId?: string;
  payload?: {
    data?: Record<string, unknown>;
    membership?: EventMembership;
  } & Record<string, unknown>;
}

// Deliberately NOT under /api/auth/ — that prefix is excluded from the proxy's
// pyloAuth, so an expired JWT would never get refreshed and the fetch would 401.
const WS_TOKEN_ENDPOINT = "/api/ws-token";
const MAX_RECONNECT_DELAY_MS = 30_000;
const BASE_RECONNECT_DELAY_MS = 1_000;
/** How often to ping an authenticated socket to detect zombies (dead but OPEN). */
const HEARTBEAT_INTERVAL_MS = 25_000;
/** How long to wait for any inbound activity after a ping before declaring the socket dead. */
const PONG_TIMEOUT_MS = 10_000;
/** Fallback so subscribe() can never hang if auth is slow or never completes. */
const CONNECT_SETTLE_TIMEOUT_MS = 5_000;

class SubscriptionManager {
  private connectionId: string | null = null;
  private webserviceUrl: string | null = null;
  private ws: WebSocket | null = null;
  private status: SubscriptionStatus = "idle";

  /** Keyed by a client-generated token so we can update/unsubscribe pre-registration. */
  private subscriptions = new Map<string, RegisteredSubscription>();

  private connecting: Promise<void> | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** True while we intend the socket to be closed (no auto-reconnect). */
  private manualClose = false;
  /**
   * Set when a socket drops so the next connect re-upserts all subs BEFORE
   * opening the socket. The server's disconnect-cache only remembers a
   * connectionId for ~3 minutes; after that (or a service restart) an open
   * with the old id is refused with `connectionNotFound` + close 4404, so
   * re-registering after auth would be too late — auth never happens.
   */
  private needsReregister = false;
  /** Set alongside a pre-connect re-register so onResync fires once auth completes. */
  private resyncPending = false;

  /** Timestamp (ms) of the last inbound message on the current socket; drives liveness. */
  private lastActivityAt = 0;
  /** Ping interval on an authenticated socket; cleared whenever the socket closes. */
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /** Deadline armed after a ping; any inbound message clears it, else the socket is dead. */
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  /** Settles the in-flight connect promise once auth completes (or times out). */
  private connectSettle: (() => void) | null = null;
  /** Timeout that force-settles the connect promise so subscribe() never hangs. */
  private connectTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Recover instantly when the network returns or the tab is refocused, rather
    // than waiting out the exponential backoff. Registered once for the tab's
    // lifetime (this is a singleton); `dispose()` removes them if ever needed.
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.handleWake);
      window.addEventListener("focus", this.handleWake);
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }
  }

  /** Remove tab-lifetime listeners and tear down the socket/timers. */
  dispose(): void {
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.handleWake);
      window.removeEventListener("focus", this.handleWake);
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }
    this.closeSocket();
  }

  getStatus(): SubscriptionStatus {
    return this.status;
  }

  /** Whether events for `entityName` are being delivered live right now. */
  isLiveFor(entityName: string): boolean {
    if (this.status !== "authenticated") return false;
    // The socket must be genuinely usable — not a zombie (dead but OPEN), not
    // mid-reregister, and having shown inbound activity within a heartbeat cycle.
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    if (this.needsReregister) return false;
    if (Date.now() - this.lastActivityAt >= HEARTBEAT_INTERVAL_MS + PONG_TIMEOUT_MS) return false;
    const target = entityName.toLowerCase();
    for (const sub of this.subscriptions.values()) {
      if (sub.entityName.toLowerCase() === target && sub.serverId) return true;
    }
    return false;
  }

  async subscribe(args: EntitySubscribeArgs): Promise<EntitySubscriptionHandle> {
    const clientKey =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : String(Math.random());

    if (!this.connectionId) {
      this.connectionId = crypto.randomUUID();
    }

    const sub: RegisteredSubscription = { ...args };
    this.subscriptions.set(clientKey, sub);

    // Register with the backend, then ensure the socket is up. Store the promise
    // so update()/reconnect serialize behind it.
    sub.registration = this.register(sub).then(() => this.ensureSocket());
    await sub.registration;

    return {
      update: async (patch) => {
        Object.assign(sub, patch);
        // Chain behind any in-flight registration to avoid interleaved upserts.
        const run = (sub.registration ?? Promise.resolve()).then(() => this.register(sub));
        sub.registration = run;
        await run;
      },
      unsubscribe: () => {
        this.subscriptions.delete(clientKey);
        void this.unregister(sub);
        if (this.subscriptions.size === 0) this.closeSocket();
      },
    };
  }

  /**
   * Delete a subscription server-side, otherwise the server keeps fetching every
   * matching event for it until the socket goes away. Waits for an in-flight
   * upsert so it can't re-create the subscription after the delete.
   */
  private async unregister(sub: RegisteredSubscription): Promise<void> {
    await sub.registration?.catch(() => undefined);
    if (!sub.serverId || !this.connectionId) return;

    try {
      await deleteEntitySubscription({ id: sub.serverId, connectionId: this.connectionId });
    } catch (error) {
      console.warn("[subscriptions] failed to delete subscription", sub.entityName, error);
    }
  }

  /** (Re-)upsert a subscription with the backend, capturing its server id + url. */
  private async register(sub: RegisteredSubscription): Promise<void> {
    if (!this.connectionId) this.connectionId = crypto.randomUUID();

    try {
      const result = await upsertEntitySubscription({
        entityName: sub.entityName,
        fieldsSelection: sub.fieldsSelection,
        filter: sub.filter,
        id: sub.serverId,
        connectionId: this.connectionId,
        onCreate: sub.onCreate,
        onUpdate: sub.onUpdate,
        onDelete: sub.onDelete,
      });
      sub.serverId = result.id;
      // The backend is the single source of truth for the public socket URL: it
      // builds webserviceUrl from WEBSOCKET_PUBLIC_URL + connectionId, so it's
      // correct per environment (local ws://localhost:9502, deploy wss://…).
      this.webserviceUrl = result.webserviceUrl;
    } catch (error) {
      console.error("[subscriptions] failed to register subscription", sub.entityName, error);
      throw error;
    }
  }

  private async fetchToken(): Promise<string | null> {
    try {
      const res = await fetch(WS_TOKEN_ENDPOINT, { credentials: "include" });
      if (!res.ok) return null;
      const body = (await res.json()) as { token?: string };
      return body.token ?? null;
    } catch (error) {
      console.error("[subscriptions] failed to fetch ws token", error);
      return null;
    }
  }

  /** Open the socket if it isn't already open/opening. */
  private async ensureSocket(): Promise<void> {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return this.connecting ?? Promise.resolve();
    }
    if (this.connecting) return this.connecting;
    if (!this.webserviceUrl) return;

    this.connecting = this.connect();
    try {
      await this.connecting;
    } finally {
      this.connecting = null;
    }
  }

  /**
   * Re-register (when needed) and then open the socket — in that order. The
   * server refuses to open a socket for a connectionId it has forgotten
   * (disconnect-cache lapsed or service restarted), so the subscriptions must
   * be re-upserted first to recreate the connection record server-side. If the
   * re-register partially fails, the open gets refused again and onclose
   * re-arms needsReregister, so the loop self-heals with backoff.
   */
  private async connect(): Promise<void> {
    if (this.needsReregister) {
      this.needsReregister = false;
      this.resyncPending = true;
      await this.reregisterAll();
    }
    // A concurrent unsubscribe/closeSocket may have emptied the manager while
    // the re-register round-trip was in flight — don't open an unwanted socket.
    if (this.manualClose || this.subscriptions.size === 0) return;
    await this.openSocket();
  }

  private openSocket(): Promise<void> {
    const url = this.webserviceUrl;
    if (!url) return Promise.resolve();

    this.manualClose = false;
    this.status = this.reconnectAttempts > 0 ? "reconnecting" : "connecting";

    return new Promise<void>((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        if (this.connectSettle === settle) this.connectSettle = null;
        if (this.connectTimeout) {
          clearTimeout(this.connectTimeout);
          this.connectTimeout = null;
        }
        resolve();
      };
      // Resolve once the connection is fully `authenticated` (see handleMessage),
      // so callers don't treat the socket as ready before it can receive events.
      // The timeout is a hard fallback so a slow/failed auth can never hang
      // subscribe(); onclose also settles so a failed connect can't hang either.
      this.connectSettle = settle;
      this.connectTimeout = setTimeout(settle, CONNECT_SETTLE_TIMEOUT_MS);

      const ws = new WebSocket(url);
      this.ws = ws;

      ws.onopen = () => {
        // Authentication proceeds async off the `connected` frame; we settle on
        // `authenticated`, not here.
      };

      ws.onmessage = (ev) => {
        if (this.ws !== ws) return;
        // Any inbound frame proves the socket is alive: refresh liveness and
        // satisfy any pending pong deadline before routing the message.
        this.lastActivityAt = Date.now();
        if (this.pongTimer) {
          clearTimeout(this.pongTimer);
          this.pongTimer = null;
        }
        let msg: WsMessage;
        try {
          msg = JSON.parse(typeof ev.data === "string" ? ev.data : "") as WsMessage;
        } catch {
          return;
        }
        void this.handleMessage(msg);
      };

      ws.onclose = () => {
        settle();
        this.handleSocketLost(ws);
      };

      ws.onerror = () => {
        // `onclose` follows and drives reconnect; nothing to do here.
      };
    });
  }

  /**
   * Tear down after `ws` died. A superseded socket's late close is ignored so
   * it can't clear the heartbeat or re-arm a reconnect for its replacement.
   */
  private handleSocketLost(ws: WebSocket): void {
    if (this.ws !== ws) return;
    this.ws = null;
    this.clearHeartbeatTimers();
    if (this.manualClose) {
      this.status = "closed";
      return;
    }
    this.needsReregister = true;
    this.scheduleReconnect();
  }

  private async handleMessage(msg: WsMessage): Promise<void> {
    switch (msg.operation) {
      case "connected":
        await this.authenticate();
        return;
      case "authenticated":
        this.status = "authenticated";
        this.reconnectAttempts = 0;
        // Start the application-level heartbeat now that we're authenticated so
        // zombie sockets (dead but readyState OPEN) get detected and recycled.
        this.startHeartbeat();
        // After a reconnect the subscriptions were re-upserted before the
        // socket opened (see connect()); now that events can flow again, let
        // each subscription backfill what it missed while the socket was down.
        // On the first connect there's nothing to backfill, so skip it.
        if (this.resyncPending) {
          this.resyncPending = false;
          this.resyncAll();
        }
        // Settle the initial connect promise: subscribe() resolves here, not on
        // socket open, so callers can rely on being able to receive events.
        this.connectSettle?.();
        return;
      case "pong":
        // Liveness heartbeat reply. Its only job is to count as inbound activity,
        // which onmessage already recorded; don't route it as an entity event.
        return;
      case "reauthenticate":
        await this.authenticate();
        return;
      case "unauthenticated":
        // Token rejected; try once more with a freshly fetched token.
        console.warn("[subscriptions] unauthenticated", msg.payload);
        this.status = "authenticating";
        await this.authenticate();
        return;
      case "connectionNotFound":
        // The server has forgotten our connectionId (disconnect-cache lapsed
        // or the service restarted) and closes with 4404 right after this
        // frame. Flag the re-register so the reconnect that onclose schedules
        // recreates the connection record before opening the next socket.
        console.warn("[subscriptions] server signalled", msg.operation, msg.payload);
        this.needsReregister = true;
        return;
      case "invalidPacket":
        console.warn("[subscriptions] server signalled", msg.operation, msg.payload);
        return;
      default:
        this.dispatchEntityEvent(msg);
    }
  }

  private async authenticate(): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.status = "authenticating";
    const token = await this.fetchToken();
    if (!token) {
      // Let the /api/graphql proxy's normal 401 → login flow handle re-auth.
      console.warn("[subscriptions] no auth token available for websocket");
      return;
    }
    // The socket may have closed (and been nulled by onclose) while the token
    // fetch was in flight — recheck before sending.
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ operation: "authenticate", payload: { authToken: token } }));
  }

  private dispatchEntityEvent(msg: WsMessage): void {
    const parsed = parseEntityEventOperation(msg.operation);
    if (!parsed || !msg.subscriptionId) return;
    const data = msg.payload?.data;
    if (!data) return;

    for (const sub of this.subscriptions.values()) {
      if (sub.serverId === msg.subscriptionId) {
        try {
          sub.onEvent(parsed.kind, data, msg.payload?.membership);
        } catch (error) {
          console.error("[subscriptions] event handler threw", error);
        }
        return;
      }
    }
  }

  private async reregisterAll(): Promise<void> {
    await Promise.all(
      Array.from(this.subscriptions.values()).map((sub) => {
        const run = (sub.registration ?? Promise.resolve())
          .catch(() => undefined)
          .then(() => this.register(sub));
        sub.registration = run;
        return run.catch(() => undefined);
      }),
    );
  }

  /** Fire each subscription's onResync so it can backfill after a reconnect. */
  private resyncAll(): void {
    for (const sub of this.subscriptions.values()) {
      try {
        sub.onResync?.();
      } catch (error) {
        console.error("[subscriptions] resync handler threw", error);
      }
    }
  }

  /**
   * Begin pinging the authenticated socket. Each tick sends a ping and arms a
   * pong deadline; any inbound activity (the pong or an entity event) satisfies
   * it. Restarts cleanly if already running so timers never double up.
   */
  private startHeartbeat(): void {
    this.clearHeartbeatTimers();
    this.lastActivityAt = Date.now();
    this.heartbeatTimer = setInterval(() => this.probeLiveness(), HEARTBEAT_INTERVAL_MS);
  }

  /** Clear both the heartbeat interval and any pending pong deadline. */
  private clearHeartbeatTimers(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  /**
   * Send a ping on the current socket and arm a pong deadline. If the deadline
   * elapses with no inbound activity the socket is a zombie: force-close it,
   * which drives onclose → scheduleReconnect (with needsReregister). Used by
   * both the heartbeat tick and the on-demand wake probe.
   */
  private probeLiveness(): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ operation: "ping" }));
    } catch {
      return;
    }
    if (this.pongTimer) return; // a deadline is already pending; inbound activity clears it
    this.pongTimer = setTimeout(() => {
      this.pongTimer = null;
      if (this.ws !== ws) return;
      try {
        ws.close();
      } catch {
        /* already closing */
      }
      // On a dead link the close handshake never completes and browsers hold
      // back onclose for up to a minute, so reconnect without waiting for it.
      this.handleSocketLost(ws);
    }, PONG_TIMEOUT_MS);
  }

  private scheduleReconnect(): void {
    if (this.manualClose || this.subscriptions.size === 0) return;
    if (this.reconnectTimer) return;

    this.status = "reconnecting";
    let delay = Math.min(
      BASE_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempts,
      MAX_RECONNECT_DELAY_MS,
    );
    // Full jitter: pick a random point in [delay/2, delay] so multiple tabs
    // don't reconnect in lockstep and thunder-herd the backend.
    delay = delay / 2 + Math.random() * (delay / 2);
    this.reconnectAttempts += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.ensureSocket();
    }, delay);
  }

  private handleVisibilityChange = (): void => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      this.handleWake();
    }
  };

  /**
   * Runs on window focus / online / tab becoming visible. If the socket looks
   * OPEN it may still be a zombie, so probe it on demand (ping + short pong
   * deadline) rather than trusting readyState; a dead socket is force-closed and
   * reconnected. If the socket isn't OPEN, fall back to the normal reconnect.
   * Arrow function for a stable listener identity.
   */
  private handleWake = (): void => {
    if (this.subscriptions.size === 0) return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.probeLiveness();
      return;
    }
    this.reconnectNow();
  };

  /**
   * Proactively re-establish the socket (e.g. after `online` fires or the tab is
   * refocused) instead of waiting out the backoff. No-op when there are no
   * subscriptions or the socket is already open/connecting. Arrow function so it
   * can be used directly as an event listener with a stable identity.
   */
  private reconnectNow = (): void => {
    if (this.subscriptions.size === 0) return;
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    if (this.connecting) return;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // The socket is down, so its server-side subs may have lapsed on TTL; reset
    // the backoff so recovery is immediate rather than resuming mid-backoff.
    this.manualClose = false;
    this.needsReregister = true;
    this.reconnectAttempts = 0;
    void this.ensureSocket();
  };

  private closeSocket(): void {
    this.manualClose = true;
    this.clearHeartbeatTimers();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
    // Settle any in-flight connect promise so a pending subscribe() can't hang.
    this.connectSettle?.();
    this.connectSettle = null;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* already closing */
      }
      this.ws = null;
    }
    this.status = "closed";
    this.reconnectAttempts = 0;
  }
}

let manager: SubscriptionManager | undefined;

/** The tab-wide singleton. Only constructed in the browser. */
export function getSubscriptionManager(): SubscriptionManager {
  if (!manager) manager = new SubscriptionManager();
  return manager;
}
